/**
 * Store de autenticación del Portal del Colaborador (Caso A).
 *
 * Independiente del store principal (authStore) — persiste en localStorage
 * con clave 'clobi-portal-auth' para no colisionar con el ERP.
 *
 * Solo activo cuando el empleado accede al portal aislado (/colaboradores).
 * El Caso B (User ERP con employee_record) usa el authStore principal.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import axios from 'axios'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PortalEmployee {
  id: string
  full_name: string
  email: string
}

interface PortalAuthState {
  accessToken: string | null
  refreshToken: string | null
  employee: PortalEmployee | null
  isAuthenticated: boolean

  // Acciones
  setTokens: (access: string, refresh: string, employee: PortalEmployee) => void
  logout: () => void
  refreshAccessToken: () => Promise<boolean>
}

// ─── Axios instance del portal (usa PortalBearer) ─────────────────────────────

export const portalApiClient = axios.create({
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
})

// Interceptor: inyectar PortalBearer en cada request
portalApiClient.interceptors.request.use((config) => {
  const { accessToken } = usePortalAuthStore.getState()
  if (accessToken) {
    config.headers.Authorization = `PortalBearer ${accessToken}`
  }
  // Base URL: usar el mismo dominio (el portal vive en el mismo subdominio del tenant)
  if (!config.baseURL) {
    const { protocol, hostname, port } = window.location
    const portSuffix = port ? `:${port}` : ''
    config.baseURL = `${protocol}//${hostname}${portSuffix}/api/v1`
  }
  return config
})

// Interceptor: auto-refresh en 401
portalApiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config
    if (error.response?.status === 401 && !originalRequest._retried) {
      originalRequest._retried = true
      const refreshed = await usePortalAuthStore.getState().refreshAccessToken()
      if (refreshed) {
        const { accessToken } = usePortalAuthStore.getState()
        originalRequest.headers.Authorization = `PortalBearer ${accessToken}`
        return portalApiClient(originalRequest)
      }
    }
    return Promise.reject(error)
  }
)

// ─── Store ────────────────────────────────────────────────────────────────────

export const usePortalAuthStore = create<PortalAuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      employee: null,
      isAuthenticated: false,

      setTokens: (access, refresh, employee) => {
        set({ accessToken: access, refreshToken: refresh, employee, isAuthenticated: true })
      },

      logout: () => {
        set({ accessToken: null, refreshToken: null, employee: null, isAuthenticated: false })
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get()
        if (!refreshToken) return false
        try {
          const res = await axios.post('/api/v1/employee/auth/refresh/', { refresh: refreshToken })
          set({ accessToken: res.data.access })
          return true
        } catch {
          get().logout()
          return false
        }
      },
    }),
    {
      name: 'clobi-portal-auth',
      storage: createJSONStorage(() => localStorage),
      // HIGH-3 (re-auditoría 2026-07-09): NO persistir tokens en localStorage
      // (robables vía XSS; el refresh de larga vida era el peor caso). El access
      // token vive solo en memoria durante la sesión; el refresh debe migrar a
      // cookie httpOnly (follow-up backend en apps/employee_portal). Al recargar,
      // el colaborador re-inicia sesión en el portal.
      partialize: (state) => ({
        employee: state.employee,
      }),
    }
  )
)
